import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { DragDropContext } from 'react-beautiful-dnd'
import { Grid, Stack, Divider } from '@mui/material_modern'
import { usePatchMultipleDispatchTicketsMutation } from 'services/dispatch'
import DispatchActiveTicketsLoadBoardColumn from 'components/containers/load-board/components/DispatchActiveTicketsLoadBoardColumn'
import { reorderItems, moveItem } from 'components/containers/load-board/utils/helperFunctions'
import { dispatchSnackbar, isDateToday } from 'utils/helpers'
import { concat } from 'lodash'
import dispatch from 'state/dispatch/index'
import { DT_STATUS_ENUM, GS_ERROR_CODE } from 'utils/constants'
import { useDispatch, useSelector } from 'react-redux'

const DispatchActiveTicketsLoadBoard = ({
  dispatchTickets,
  requestedDispatchTickets,
  drivers,
  selectedDate,
  showRequestedTickets,
}) => {
  const [driverLists, setDriverLists] = useState({})
  const [pastDriverLists, setPastDriverLists] = useState({})
  const [sortedRequestedTickets, setSortedRequestedTickets] = useState([])
  const dispatch = useDispatch()

  const [patchMultipleDispatchTickets] = usePatchMultipleDispatchTicketsMutation()

  useEffect(() => {
    setSortedRequestedTickets(
      requestedDispatchTickets.sort((a, b) => {
        if (!a.requestedScheduleDate && !b.requestedScheduleDate) return 0
        if (!a.requestedScheduleDate) return -1
        if (!b.requestedScheduleDate) return 1
        return new Date(a.requestedScheduleDate) - new Date(b.requestedScheduleDate)
      }),
    )
  }, [requestedDispatchTickets])

  useEffect(() => {
    const newDriverLists = {}
    const newDriverListsPast = {}

    let currentTickets = []
    let pastTickets = []
    dispatchTickets.forEach(ticket => {
      const isToday = isDateToday(ticket.scheduleDate, selectedDate)
      if (isToday) {
        currentTickets.push(ticket)
      } else {
        pastTickets.push(ticket)
      }
    })

    drivers.forEach(driver => {
      newDriverLists[driver.id] = currentTickets
        .filter(ticket => ticket.driverId === driver.id)
        .sort((a, b) => a.loadBoardIndex - b.loadBoardIndex)
      newDriverListsPast[driver.id] = pastTickets
        .filter(ticket => ticket.driverId === driver.id)
        .sort((a, b) => a.loadBoardIndex - b.loadBoardIndex)

      // Remove the assigned tickets from the holding arrays, leaving only the unassigned and the ones assigned to nonexistent drivers
      currentTickets = currentTickets.filter(
        ticket => ticket.status !== DT_STATUS_ENUM.REQUESTED && ticket.driverId !== driver.id,
      )
      pastTickets = pastTickets.filter(
        ticket => ticket.status !== DT_STATUS_ENUM.REQUESTED && ticket.driverId !== driver.id,
      )
    })
    newDriverLists.unassigned = currentTickets.sort((a, b) => a.loadBoardIndex - b.loadBoardIndex)
    newDriverListsPast.unassigned = pastTickets.sort((a, b) => a.loadBoardIndex - b.loadBoardIndex)
    setDriverLists(newDriverLists)
    setPastDriverLists(newDriverListsPast)
  }, [dispatchTickets, drivers])

  const handlePatchError = error => {
    let errorMessage = `There was an error updating the ticket.`

    if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
      errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
    }

    dispatchSnackbar(dispatch, 'error', errorMessage)
  }

  const onDragEnd = async result => {
    const { source: sourceInfo, destination: destinationInfo } = result
    if (!destinationInfo) return // dropped outside the list
    if (destinationInfo.droppableId?.endsWith('past')) return // dropped in a past list

    // If dropped in requested
    if (destinationInfo.droppableId === 'requested') {
      dispatchSnackbar(dispatch, 'error', `Unable to move a ticket back to requested once scheduled`)
      return
    }

    if (sourceInfo.droppableId === destinationInfo.droppableId) {
      const { updatedSourceList, ticketsToPatch } = reorderItems({
        sourceList: [...(driverLists[sourceInfo.droppableId] || [])],
        startIndex: sourceInfo.index,
        endIndex: destinationInfo.index,
      })
      setDriverLists(previousDriverLists => ({
        ...previousDriverLists,
        [sourceInfo.droppableId]: updatedSourceList,
      }))

      if (Array.isArray(ticketsToPatch) && ticketsToPatch.length > 0) {
        await patchMultipleDispatchTickets({
          body: ticketsToPatch,
        }).catch(handlePatchError)
      }
    } else {
      let sourceList = []

      if (sourceInfo.droppableId === 'requested') {
        sourceList = sortedRequestedTickets || []
      } else if (sourceInfo.droppableId.endsWith('past')) {
        const driverId = sourceInfo.droppableId.slice(0, -4)
        sourceList = [...(pastDriverLists?.[driverId] || [])]
      } else {
        sourceList = [...(driverLists[sourceInfo.droppableId] || [])]
      }

      const { updatedSourceList, updatedDestinationList, ticketsToPatch } = moveItem({
        sourceList: sourceList,
        destinationList: [...(driverLists[destinationInfo.droppableId] || [])],
        sourceInfo,
        destinationInfo,
        selectedDate,
      })

      if (sourceInfo.droppableId.endsWith('past')) {
        setDriverLists(previousDriverLists => ({
          ...previousDriverLists,
          [destinationInfo.droppableId]: updatedDestinationList,
        }))
        setPastDriverLists(previousPastDriverLists => ({
          ...previousPastDriverLists,
          [sourceInfo.droppableId.slice(0, -4)]: updatedSourceList,
        }))
      } else if (sourceInfo.droppableId.endsWith('requested')) {
        setDriverLists(previousDriverLists => ({
          ...previousDriverLists,
          [destinationInfo.droppableId]: updatedDestinationList,
        }))
        setSortedRequestedTickets(updatedSourceList)
      } else {
        setDriverLists(previousDriverLists => ({
          ...previousDriverLists,
          [destinationInfo.droppableId]: updatedDestinationList,
          [sourceInfo.droppableId]: updatedSourceList,
        }))
      }

      await patchMultipleDispatchTickets({
        body: ticketsToPatch,
      }).catch(handlePatchError)
    }
  }

  return (
    <Grid>
      <DragDropContext onDragEnd={onDragEnd}>
        <Stack direction="row">
          {showRequestedTickets && sortedRequestedTickets && sortedRequestedTickets.length > 0 && (
            <DispatchActiveTicketsLoadBoardColumn
              droppableId="requested"
              driverInfo={{ name: 'Requested Tickets' }}
              currentTickets={sortedRequestedTickets}
            />
          )}
          {showRequestedTickets && sortedRequestedTickets && sortedRequestedTickets.length > 0 && (
            <Divider orientation="vertical" flexItem />
          )}
          <DispatchActiveTicketsLoadBoardColumn
            droppableId="unassigned"
            driverInfo={{ name: 'Unassigned Tickets' }}
            currentTickets={
              isDateToday(selectedDate)
                ? driverLists?.['unassigned']
                : concat(driverLists?.['unassigned'], pastDriverLists?.['unassigned'])
            }
            pastTickets={isDateToday(selectedDate) ? pastDriverLists?.['unassigned'] : []}
          />
          <Stack direction="row" sx={{ overflowX: 'scroll' }}>
            {drivers.map(driver => (
              <React.Fragment key={driver.id}>
                <Divider orientation="vertical" flexItem />
                <DispatchActiveTicketsLoadBoardColumn
                  droppableId={driver.id}
                  driverInfo={driver}
                  currentTickets={
                    isDateToday(selectedDate)
                      ? driverLists?.[driver.id]
                      : concat(driverLists?.[driver.id], pastDriverLists?.[driver.id])
                  }
                  pastTickets={isDateToday(selectedDate) ? pastDriverLists?.[driver.id] : []}
                />
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      </DragDropContext>
    </Grid>
  )
}

DispatchActiveTicketsLoadBoard.propTypes = {
  dispatchTickets: PropTypes.array.isRequired,
  requestedDispatchTickets: PropTypes.array,
  drivers: PropTypes.array.isRequired,
}

DispatchActiveTicketsLoadBoard.defaultProps = {
  requestedDispatchTickets: [],
}

export default DispatchActiveTicketsLoadBoard
